import { useCallback } from 'react'

import {
  InfiniteData,
  type QueryClient,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'

import { useAuthInfo } from '@sweetauth/core'

import { API_URL } from '@/constants/env'

import { convertDatesInObject, handleUpdateData } from '@/helpers/api'
import { SweetspotError } from '@/helpers/error'

import { useHandleError } from '@/hooks/common/useErrorHandler'
import { pursuitEstimatedValueSummaryQueryKeys } from '@/hooks/pursuits/summary/types'
import {
  pursuitActionsQueryKeys,
  pursuitQueryKeys,
  pursuitStageDueDatesQueryKeys,
  pursuitsQueryKeys,
  recentActivityQueryKeys,
  winLossQueryKeys,
} from '@/hooks/pursuits/types'

import { useFederalContractSearchStore } from '@/stores/FederalContractSearchStore'
import { useFederalGrantSearchStore } from '@/stores/FederalGrantSearchStore'
import { useSbirSearchStore } from '@/stores/SbirSearchStore'
import { useSledContractSearchStore } from '@/stores/SledContractSearchStore'

import type { PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { Pursuit, UpdatePursuit } from '@/types/pursuit/Pursuit'
import type { PursuitStageDueDateSet } from '@/types/pursuit/StageDueDate'

interface UseUpdatePursuitProps {
  orgId: UUID | ''
  optimisticUpdate?: boolean
  bustSearchCacheOnSuccess?: boolean
}

export interface UpdatePursuitArgs {
  pursuit: Pursuit
  reasoning?: string | null
  stageDueDates?: PursuitStageDueDateSet[]
  /**
   * Roster writes are OPT-IN: only dialogs where the user edited the
   * Owners/assignees field should set this. Every other mutation spreads a
   * (staleTime: Infinity) cached pursuit, and on a private pursuit the
   * roster IS the access list — blind-writing a stale user_ids silently
   * revokes members added concurrently elsewhere. Omitted user_ids leaves
   * the roster untouched on the backend.
   */
  writeRoster?: boolean
}

export const updatePursuit = async (
  args: UpdatePursuitArgs,
  accessToken: string | undefined | null,
): Promise<Pursuit> => {
  if (accessToken === null || accessToken === undefined) {
    throw new Error(`No access token provided.`)
  }
  const { pursuit, reasoning, stageDueDates, writeRoster = false } = args
  let pwin_override = pursuit.pwin_override
  // order matters here, want to check for empty string first,
  // otherwise the Math.floor will default to zero
  // @ts-expect-error
  if (pursuit.pwin_override === '') {
    pwin_override = null
  } else if (pursuit.pwin_override !== null) {
    pwin_override = Math.floor(pursuit.pwin_override)
  }
  const body: UpdatePursuit = {
    title: pursuit.title,
    description: pursuit.description,
    due_date: pursuit.due_date,
    estimated_value: pursuit.estimated_value,
    terminal_status: pursuit.terminal_status,
    stage_id: pursuit.stage.id,
    user_ids: writeRoster ? pursuit.user_ids : undefined,
    // must be integers
    pwin: pursuit.pwin,
    pwin_override: pwin_override,
    win_themes: pursuit.win_themes,
    archived: pursuit.archived,
    tag_ids: pursuit.tag_ids,
    extended_schema_id: pursuit.extended_schema_id ?? null,
    extended_data: pursuit.extended_data ?? null,
    ...(reasoning !== undefined ? { reasoning } : {}),
    ...(stageDueDates ? { stage_due_dates: stageDueDates } : {}),
  }
  const res: Response = await fetch(
    `${API_URL}/api/v1/pursuits/${pursuit.id}`,
    {
      method: 'PUT',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    },
  )
  if (!res.ok) {
    throw new SweetspotError({
      name: 'UPDATE_PURSUIT_ERROR',
      message: res.statusText,
      cause: {
        status: res.status,
      },
    })
  }
  return convertDatesInObject(await res.json())
}

export const useUpdateSearchCache = () => {
  // TODO: this is filthy, and when we refactor how search works, we should make sure to refactor this
  // and remove the updatePursuit hook's dependence on this to bust/update the search cache
  const federalContractStore = useFederalContractSearchStore()
  const federalGrantStore = useFederalGrantSearchStore()
  const sbirStore = useSbirSearchStore()
  const sledContractStore = useSledContractSearchStore()

  const updateSearchCache = useCallback(
    (pursuit: Pursuit) => {
      const type = pursuit.type
      switch (type) {
        case 'federal_contract_pursuit':
          if (
            pursuit.federal_contract_pursuit &&
            pursuit.federal_contract_pursuit.entity_id
          ) {
            federalContractStore.addCreatedPursuit(
              pursuit.federal_contract_pursuit.entity_id,
              pursuit.id,
              pursuit.stage.id,
            )
          }
          break
        case 'federal_grant_pursuit':
          if (
            pursuit.federal_grant_pursuit &&
            pursuit.federal_grant_pursuit.entity_id
          ) {
            federalGrantStore.addCreatedPursuit(
              pursuit.federal_grant_pursuit.entity_id,
              pursuit.id,
              pursuit.stage.id,
            )
          }
          break
        case 'sbir_pursuit':
          if (pursuit.sbir_pursuit && pursuit.sbir_pursuit.entity_id) {
            sbirStore.addCreatedPursuit(
              pursuit.sbir_pursuit.entity_id,
              pursuit.id,
              pursuit.stage.id,
            )
          }
          break
        case 'sled_contract_pursuit':
          if (
            pursuit.sled_contract_pursuit &&
            pursuit.sled_contract_pursuit.entity_id
          ) {
            sledContractStore.addCreatedPursuit(
              pursuit.sled_contract_pursuit.entity_id,
              pursuit.id,
              pursuit.stage.id,
            )
          }
          break
        case 'dibbs_contract_pursuit':
          // TODO: implement
          break
        default:
          const exhaustiveCheck: never = type
          return exhaustiveCheck
      }
    },
    [
      federalContractStore.addCreatedPursuit,
      federalGrantStore.addCreatedPursuit,
      sbirStore.addCreatedPursuit,
      sledContractStore.addCreatedPursuit,
    ],
  )

  return updateSearchCache
}

const ACTION_LOG_REFRESH_DELAYS = [3000] as const
const ACTION_LOG_REASONING_REFRESH_DELAYS = [1500, 3000, 6000, 10000] as const

export const schedulePursuitActionRefreshes = ({
  queryClient,
  pursuitId,
  includeReasoningRetry,
}: {
  queryClient: QueryClient
  pursuitId: UUID
  includeReasoningRetry: boolean
}) => {
  const actionsKey = pursuitActionsQueryKeys.pursuit({ pursuitId })
  const delays = includeReasoningRetry
    ? ACTION_LOG_REASONING_REFRESH_DELAYS
    : ACTION_LOG_REFRESH_DELAYS

  for (const delay of delays) {
    setTimeout(() => {
      void queryClient.invalidateQueries({ queryKey: actionsKey })
      void queryClient.invalidateQueries({
        queryKey: recentActivityQueryKeys.all(),
      })
      void queryClient.invalidateQueries({
        queryKey: winLossQueryKeys.all(),
      })
    }, delay)
  }
}

export const useUpdatePursuit = ({
  orgId,
  optimisticUpdate = false,
}: UseUpdatePursuitProps) => {
  const user = useAuthInfo()
  const queryClient = useQueryClient()
  const { handleError } = useHandleError()

  const updateSearchCache = useUpdateSearchCache()

  return useMutation<
    Pursuit,
    Error,
    UpdatePursuitArgs,
    | InfiniteData<PaginatedResponse<Pursuit>>
    | PaginatedResponse<Pursuit>
    | undefined
  >({
    mutationFn: (args) => updatePursuit(args, user?.accessToken),
    onMutate: async (args) => {
      const { pursuit } = args
      // cancel any outgoing refetches (so they don't overwrite our optimistic update)
      const key = pursuitsQueryKeys.organization({ orgId })
      const previousData = await handleUpdateData(queryClient, key, pursuit)

      if (optimisticUpdate) {
        queryClient.cancelQueries({ queryKey: key })
        const individualPursuitKey = pursuitQueryKeys.pursuit({
          pursuitId: pursuit.id,
        })
        queryClient.setQueryData(individualPursuitKey, pursuit)
      }

      // return rollback value
      return previousData
    },
    onError: (err, args, context) => {
      const { pursuit } = args
      // rollback to the previous value
      const key = pursuitsQueryKeys.organization({ orgId })
      queryClient.setQueryData(key, context)

      if (optimisticUpdate) {
        // rollback the individual pursuit
        const individualPursuitKey = pursuitQueryKeys.pursuit({
          pursuitId: pursuit.id,
        })
        let oldPursuit: Pursuit | undefined
        if (context) {
          // Cache may be PaginatedResponse or InfiniteData depending on caller.
          const pages =
            'pages' in context ? context.pages : [{ data: context.data }]
          for (const page of pages) {
            oldPursuit = page.data.find((p) => p.id === pursuit.id)
          }
        }
        if (oldPursuit) {
          queryClient.setQueryData(individualPursuitKey, oldPursuit)
        }
      }

      handleError(err)
    },
    onSuccess: (data, args) => {
      const { pursuit } = args
      // the optimistic update is already in place, no need to update again
      const key = pursuitsQueryKeys.organization({ orgId })
      queryClient.invalidateQueries({ queryKey: key })
      // Keep the multi-org overview cache in sync so a single-org update
      // (e.g. from the pursuit detail page) reflects on orgs/overview.
      queryClient.invalidateQueries({
        queryKey: pursuitsQueryKeys.organizationsOverviewAll(),
      })
      queryClient.invalidateQueries({
        queryKey: pursuitEstimatedValueSummaryQueryKeys.organization({ orgId }),
      })

      schedulePursuitActionRefreshes({
        queryClient,
        pursuitId: pursuit.id,
        includeReasoningRetry: Boolean(args.reasoning),
      })

      updateSearchCache(pursuit)

      // set the data for the individual pursuit
      const individualPursuitKey = pursuitQueryKeys.pursuit({
        pursuitId: pursuit.id,
      })
      queryClient.setQueryData(individualPursuitKey, data)

      // Invalidate stage due dates cache if they were updated
      if (args.stageDueDates) {
        return queryClient.invalidateQueries({
          queryKey: pursuitStageDueDatesQueryKeys.pursuit({
            pursuitId: pursuit.id,
          }),
        })
      }
    },
  })
}
